'use client';

import Image from 'next/image';
import {
  useState,
} from 'react';

import TuiCruisesLogo from '../../../../../ui-kit/assets/logos/tui-cruises.svg';

import NavigationCollection from './navigation-collection';

import type { IconName } from '../../../core-components/Icon';
import type { LegacyIconName } from '../LegacyIcon';
import type {
  Dispatch,
  FC,
  MouseEventHandler,
  SetStateAction} from 'react';

type NavigationCollectionItemBase = {
  title: string;
  href?: HTMLAnchorElement['href'];
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  desktop?: boolean;
  mobile?: boolean;
};

type NavigationCollectionItemCommon = NavigationCollectionItemBase & {
  type: 'badgeSale' | 'badgeSaleRose' | 'badgeInclusive' | 'meta';
  icon: LegacyIconName | IconName;
  isLegacyIcon?: boolean;
};

export type NavigationCollectionItemMainChild = NavigationCollectionItemBase & {
  icon: LegacyIconName;
  isLegacyIcon?: boolean;
};

type NavigationCollectionItemMain = NavigationCollectionItemBase & {
  type: 'main';
  icon?: LegacyIconName;
  isLegacyIcon?: boolean;
  children?: NavigationCollectionItemMainChild[];
};

export type NavigationCollectionItem =
  | NavigationCollectionItemCommon
  | NavigationCollectionItemMain;

type NavigationClickHandler = MouseEventHandler<
  HTMLAnchorElement | HTMLButtonElement
>;

type Props = {
  closeNavigation: () => void;
  items: NavigationCollectionItem[];
};

type NavigationClickHandlerWrapper = (
  handler?: NavigationClickHandler,
) => NavigationClickHandler;

/**
 * Creates a toggle function for a child navigation item of given index.
 */
const createChildToggle = (
  idx: number,
  setIdx: Dispatch<SetStateAction<number>>,
) => {
  return () => {
    setIdx(prev => {
      if (prev === idx) {
        return -1;
      }

      return idx;
    });
  };
};

/**
 * Renders a navigation item based on its type.
 */
const renderNavigationItem = (
  idx: number,
  activeNavIdx: number,
  setActiveNavIdx: Dispatch<SetStateAction<number>>,
  createNavigationClickHandler: NavigationClickHandlerWrapper,
  item: NavigationCollectionItem,
) => {
  if (item.type === 'main') {
    return (
      <NavigationCollection.Main
        key={item.title}
        itemTitle={item.title}
        itemIcon={item.icon}
        isLegacyIcon={item.isLegacyIcon}
        href={item.href}
        isActive={activeNavIdx === idx}
        onClick={createChildToggle(idx, setActiveNavIdx)}
        desktop={item.desktop}
        mobile={item.mobile}
      >
        {(item.children ?? []).map(child => (
          <NavigationCollection.MainChild
            key={child.title}
            itemTitle={child.title}
            itemIcon={child.icon}
            isLegacyIcon={item.isLegacyIcon}
            href={child.href}
            onClick={createNavigationClickHandler(child.onClick)}
            desktop={item.desktop}
            mobile={item.mobile}
          />
        ))}
      </NavigationCollection.Main>
    );
  }

  let ComponentName = null;

  if (item.type === 'badgeSale') {
    ComponentName = NavigationCollection.BadgeSale;
  }

  if (item.type === 'badgeSaleRose') {
    ComponentName = NavigationCollection.BadgeSaleRose;
  }

  if (item.type === 'badgeInclusive') {
    ComponentName = NavigationCollection.BadgeInclusive;
  }

  if (item.type === 'meta') {
    ComponentName = NavigationCollection.Meta;
  }

  if (ComponentName === null) {
    ComponentName = NavigationCollection.Meta;
  }

  return (
    <ComponentName
      key={`${idx}${item.title}`}
      itemTitle={item.title}
      itemIcon={item.icon}
      isLegacyIcon={item.isLegacyIcon}
      href={item.href}
      onClick={createNavigationClickHandler(item.onClick)}
      desktop={item.desktop}
      mobile={item.mobile}
    />
  );
};

/**
 * Renders the main navigation items inside a NavigationCollection.
 */
export const MainNavigation: FC<Props> = ({ closeNavigation, items }) => {
  const [activeNavIdx, setActiveNavIdx] = useState(-1);

  const createNavigationClickHandler: NavigationClickHandlerWrapper =
    handler => {
      return ev => {
        closeNavigation();

        if (handler) {
          handler(ev);
        }
      };
    };

  return (
    <ul className="m-0 block list-none p-0 lg:flex lg:justify-evenly">
      {items.map((item, idx) =>
          renderNavigationItem(
            idx,
            activeNavIdx,
            setActiveNavIdx,
            createNavigationClickHandler,
            item,
          ),
        )}
      <NavigationCollection.Image>
        <Image
          src={TuiCruisesLogo}
          width={114}
          height={28}
          alt="TUI Cruises Logo"
        />
      </NavigationCollection.Image>
    </ul>
  );
};
